import {themesData} from 'data/themes-data';

/**
 * Get current game step id of group
 * @param {object} themeData 
 * @param {object} groupData 
 * @returns 
 */
function getCurrentGameStepId(themeData, groupData) {
	let currentGameStepId = themeData.gameSteps[0].id;

	if (
		groupData.currentGameStepId &&
		themeData.gameSteps.some((t) => {return t.id === groupData.currentGameStepId;})
	) {
		currentGameStepId = themeData.gameSteps.find((t) => {return t.id === groupData.currentGameStepId;}).id;
	}

	return currentGameStepId;
};


/**
 * Get prev game step id of group
 * @param {string} currentGameStepId
 * @param {object} themeData 
 * @returns 
 */
function getPrevGameStepId(currentGameStepId, themeData) {

	const gameSteps = themeData.gameSteps ? themeData.gameSteps : [];
	const currentGameStepIndex = gameSteps.findIndex((gs) => {return gs.id === currentGameStepId;});
	
	const prevGameStepId = (currentGameStepIndex > 0 ? gameSteps[currentGameStepIndex - 1].id : null); 



	return prevGameStepId;
};

/**
 * Get group progress in percent
 * @param {string} themeId 
 * @param {object} groupData 
 * @returns 
 */
function getGroupProgress(themeId, groupData) {
	let progress = 0;

	const themeData = themesData.find((theme) => {return theme.id === themeId;});
	if (themeData && themeData.gameSteps && themeData.gameSteps.length > 0) {
		const gameStepId = getCurrentGameStepId(themeData, groupData);
		const gameStepIndex = themeData.gameSteps.findIndex((gs) => {return gs.id === gameStepId;});
		progress = Math.round((gameStepIndex / (themeData.gameSteps.length - 1)) * 100);
	}

	return progress;
};

/**
 * Get video id
 * Can depend on previous answers to questions
 * @param {string} languageId 
 * @param {object} gameStepData 
 * @param {object} groupData 
 * @returns 
 */
function getVideoId(languageId, gameStepData, groupData) {
	let videoId = null;
	let thumbnail = null;
	if (gameStepData.videos && gameStepData.videos.length > 0) {
		if (gameStepData.videos.length === 1) {
			/* Only one video, use that */
			videoId = gameStepData.videos[0].id[languageId];
			thumbnail = gameStepData.videos[0].thumbnail;
		} else {
		/* Multiple videos, check conditions */
			gameStepData.videos.forEach((video) => {
			/* Video id already found */
				if (videoId) return;

				if (video.conditions && video.conditions.length > 0) {
					let conditionsAreMet = true;
					video.conditions.forEach((condition) => {
						if (!conditionsAreMet) return;

						if (!checkIfConditionIsMet(condition, groupData)) {
							conditionsAreMet = false;
						}
					});

					if (conditionsAreMet) {
						videoId = video.id[languageId];
						thumbnail = video.thumbnail;
					}
				}
			});
		}
	}

	return {videoId, thumbnail};
};


/**
 * Check if next btn is displayed,
 * Can depend on whether a group has answered certain questions
 * @param {object} gameStepData 
 * @param {object} groupData 
 * @returns 
 */
function checkIfNextBtnIsDisplayed(gameStepData, groupData) {
	let showNextStepBtn = (gameStepData.nextBtn ? true : false);

	if (
		showNextStepBtn && 
		gameStepData && 
		gameStepData.nextBtnConditions && 
		gameStepData.nextBtnConditions.length > 0
	) {
		gameStepData.nextBtnConditions.forEach((condition) => {
			if (!showNextStepBtn) return;

			if (!checkIfConditionIsMet(condition, groupData)) {
				showNextStepBtn = false;
			}
		});
	}

	return showNextStepBtn;
};

/**
 * Check if conditional next step id,
 * Can depend on whether a group has answered certain questions
 * @param {object} gameStepData 
 * @param {object} groupData 
 * @returns 
 */
function checkIfConditionalNextStepId(gameStepData, groupData) {
	let conditionsAreMet = false;
	if (
		gameStepData && 
		gameStepData.nextStepIdConditions && 
		gameStepData.nextStepIdConditions.length > 0
	) {
		gameStepData.nextStepIdConditions.forEach((condition) => {
			if (conditionsAreMet) return;

			if (checkIfConditionIsMet(condition, groupData)) {
				conditionsAreMet = true;
			}
		});
	}

	return conditionsAreMet;
};



/**
 * Check if a condition is met
 * @param {object} condition 
 * @param {object} groupData 
 */
function checkIfConditionIsMet(condition, groupData) {
	/* Question has been answered */
	if (condition.type === 'question-answered') {
		if (
			groupData.questions &&
			groupData.questions.some((q) => {
				return (q.questionId === condition.questionId && q.optionId !== null);
			})
		) {
			return true;
		}
	}


	/* Specific option selected */
	if (condition.type === 'question-option-selected') {
		if (
			groupData.questions &&
			groupData.questions.some((q) => {
				return (
					q.questionId === condition.questionId &&
					q.optionId === condition.optionId
				);
			})
		) {
			return true;
		}
	}

	return false;
};


/**
 * Get results for group surveys
 * @param {object} gameData 
 * @param {array} groupsData 
 * @returns 
 */
function getGameResults(gameData, groupsData) {
	/* Get theme data (default is first theme) */
	const themeData = themesData.find((t) => {return t.id === gameData.themeId;});

	/* Get results (each result is for a single page) */
	const results = (themeData.resultSteps ? JSON.parse(JSON.stringify(themeData.resultSteps)) : []);
	if (themeData && themeData.gameSteps && themeData.gameSteps.length > 0) {
		results.forEach((result) => {
			if (result.type === 'result') {
				result.result = null;
				/* Get relevant game step data for this result page */
				const gameStepData = themeData.gameSteps.find((step) => {
					return result.gameStepId === step.id;
				});
				if (gameStepData && gameStepData.questions && gameStepData.questions.length > 0) {
					const question = gameStepData.questions.find((question) => {
						return question.id === result.questionId;
					});
					if (question) {
						const resultObj = {
							type: result.type, 
							questionId: question.id, 
							text: (question.text ? question.text : null), 
							options: [], 
							total: 0
						};
						/* Get data for all options of question */
						question.options.forEach((option) => {
							const optionObj = {optionId: option.id, text: (option.text ? option.text : null), value: 0};
							resultObj.options.push(optionObj);
						});

						if (gameStepData.type === 'survey') {
							const answers = gameData.surveyAnswers ? gameData.surveyAnswers : [];
							const questionAnswers = answers.filter((a) => {
								return (a.gameStepId === gameStepData.id && a.questionId === question.id);
							});
							questionAnswers.forEach((answer) => {
								if (answer.optionIds) {
									resultObj.total += answer.optionIds.length;

									answer.optionIds.forEach((optionId) => {
										const resultOptionsIndex = resultObj.options.findIndex((o) => {
											return o.optionId === optionId;
										});
										if (resultOptionsIndex >= 0) {
											resultObj.options[resultOptionsIndex].value += 1;
										}
									});
								}
							});
							result.result = resultObj;
						} else {
							console.error('unhandled exception');
						}
					}
				}
			}
		});
	}

	return results;
}



export {
	getCurrentGameStepId,
	getPrevGameStepId,
	getGroupProgress,
	getVideoId,
	checkIfNextBtnIsDisplayed,
	getGameResults,
	checkIfConditionalNextStepId
};